/**
 * Codabar (NW-7) value normalizer.
 *
 * Codabar accepts: digits 0-9, symbols `- $ : / . +`, and start/stop letters A B C D.
 *
 * Behaviour:
 * - Logs a console warning in development when invalid characters are present
 *   but does NOT throw — the underlying jsbarcode lib will surface the final
 *   error and we don't want to crash the canvas runtime.
 * - When `autoWrap` is true and the value does not already begin/end with a
 *   Codabar start/stop letter (A/B/C/D), the value is wrapped with `A...A`.
 *
 * Pure function: safe to import from both web and native renderers.
 */
const CODABAR_VALID_CHARS = /^[0-9\-$:/.+ABCD]+$/i;
const CODABAR_START_STOP = /^[ABCD]/i;
const CODABAR_END_STOP = /[ABCD]$/i;

export function normalizeCodabarValue(
  rawValue: string,
  autoWrap: boolean
): string {
  const value = String(rawValue ?? '');

  if (value.length === 0) {
    return value;
  }

  if (
    !CODABAR_VALID_CHARS.test(value) &&
    process.env.NODE_ENV !== 'production'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[BarCode] Codabar value contains characters outside [0-9 - $ : / . + A B C D]: "${value}"`
    );
  }

  if (!autoWrap) {
    return value;
  }

  const hasStart = CODABAR_START_STOP.test(value);
  const hasEnd = CODABAR_END_STOP.test(value);

  if (hasStart && hasEnd) {
    return value;
  }

  const prefix = hasStart ? '' : 'A';
  const suffix = hasEnd ? '' : 'A';
  return `${prefix}${value}${suffix}`;
}

/**
 * Map our stored format identifier to the encoder key JsBarcode expects.
 *
 * JsBarcode registers the Codabar encoder under the lowercase key `codabar`,
 * but our config persists the user-facing label `Codabar`. Passing the
 * mixed-case value makes `JsBarcode[format]` undefined and the lib throws
 * `n.options(...)[r.format] is not a function` at render time. Lowercase
 * only that one value; other formats already match JsBarcode's keys.
 */
export function toJsBarcodeFormat(format: string): string {
  return format === 'Codabar' ? 'codabar' : format;
}

export default normalizeCodabarValue;
